import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { FileText, Link2, Search, CheckCircle2, Loader2 } from 'lucide-react';

const API = '/api';

/**
 * Pick a policy from this workspace and link it as evidence on the given
 * control + evidenceIndex slot. Calls POST /api/files/attach-policy.
 */
const AttachPolicyDialog = ({ open, onClose, auditId, controlId, evidenceIndex, onAttached, controlFrameworks = [] }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedPolicyId(null);
    const fetchPolicies = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/policies`, { headers: { Authorization: `Bearer ${token}` } });
        const all = res.data || [];
        // Exclude archived; published/draft are both acceptable as evidence references.
        setPolicies(all.filter(p => p.status !== 'archived'));
      } catch (err) {
        console.error('Failed to load policies', err);
        toast.error('Failed to load policies');
      } finally {
        setLoading(false);
      }
    };
    fetchPolicies();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = policies;
    if (q) {
      list = list.filter(p => {
        const name = (p.name || p.title || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }
    // Surface framework-matching policies first when the control has frameworks.
    if (controlFrameworks?.length) {
      const fwSet = new Set(controlFrameworks);
      list = [...list].sort((a, b) => {
        const aHit = (a.frameworks || []).some(f => fwSet.has(f)) ? 0 : 1;
        const bHit = (b.frameworks || []).some(f => fwSet.has(f)) ? 0 : 1;
        return aHit - bHit;
      });
    }
    return list;
  }, [policies, search, controlFrameworks]);

  const handleAttach = async () => {
    if (!selectedPolicyId) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/files/attach-policy`,
        { audit_id: auditId, control_id: controlId, evidence_index: evidenceIndex, policy_id: selectedPolicyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Linked policy: ${res.data.policy_name}`);
      onAttached?.(res.data);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link policy');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col" data-testid="attach-policy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Link2 className="w-5 h-5 text-navy-600" />
            Link a policy as evidence
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Attach an existing policy document to this evidence requirement. The policy will appear
            alongside uploaded files and counts toward the evidence count for this control.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search policies by name or description…"
            className="pl-9"
            data-testid="attach-policy-search"
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-[200px] border border-slate-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading policies…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No policies match your search.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="attach-policy-list">
              {filtered.map((p) => {
                const id = p.id;
                const name = p.name || p.title || 'Untitled policy';
                const selected = selectedPolicyId === id;
                return (
                  <li
                    key={id}
                    className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors ${selected ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                    onClick={() => setSelectedPolicyId(id)}
                    data-testid={`attach-policy-item-${id}`}
                  >
                    <div className={`w-4 h-4 mt-0.5 rounded-full flex-shrink-0 border ${selected ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300'}`}>
                      {selected && <CheckCircle2 className="w-4 h-4 text-white -m-px" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 truncate">{name}</span>
                        <Badge variant={p.status === 'published' ? 'default' : 'secondary'} className="text-[10px] uppercase tracking-wide">
                          {p.status}
                        </Badge>
                        {(p.frameworks || []).slice(0, 3).map(fw => (
                          <Badge key={fw} variant="outline" className="text-[10px] px-1.5 py-0">{fw}</Badge>
                        ))}
                      </div>
                      {p.description && (
                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{p.description}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="attach-policy-cancel">Cancel</Button>
          <Button
            onClick={handleAttach}
            disabled={!selectedPolicyId || submitting}
            className="bg-navy-600 hover:bg-navy-700 text-white"
            data-testid="attach-policy-submit"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Linking…</>) : 'Link policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttachPolicyDialog;
